import _ from 'lodash';
// @mui
import { Stack } from '@mui/system';
import { Box, Button, Typography, useTheme } from '@mui/material';
//components
import FormCreator from 'components/FormCreator';
import { fieldType, validationTypes, invalidFields, fieldsGroupType } from 'components/FormCreator/types';
import { FormButton } from 'theme/elements/Buttons';
//reducers
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
import { getNewBeneficiary, setBeneficiary } from 'state/reducers/beneficiary.reducers';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
//sections
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1 } from 'theme/elements/Headers';
//constants
import { Beneficiary } from 'constants/beneficiary.constants';
//services
import { getPaymentsOptions } from 'services/beneficiary.services';
import { HeaderCaption1, HeaderPage4 } from 'theme/elements/Headers';
import { acceptableFileFormats, maxFileSize } from 'constants/global.constants';
import { CheckCircleOutlineRounded, UploadFile } from '@mui/icons-material';
import ErrorBox from 'components/ErrorBox';
import { balanceTransStepsPrefix } from 'constants/balance.constants';
import { useState } from 'react';
import { getUserDetails, getUserPermission } from 'state/reducers/user.reducers';
import { User, UserPermission } from 'constants/user.constants';
import { validateFileExtensions } from 'utils';
import { uploadPaymentFile } from 'services/pay.services';

export default function TaxShelterForm(props: {
  handleChange: any;
  invalidFields: invalidFields;
  setInvalidFields: any;
  isCountryDisabled: boolean;
}) {
  const t = useAppSelector(selectTranslations);
  const beneficiary: Beneficiary = useAppSelector(getNewBeneficiary);
  const prefixFields = 'page.beneficiary.create.step.2.';
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const imageUrl = useAppSelector(selectImageUrl);
  const [showErrorBox, setShowErrorBox] = useState(false);
  const [fileUpload, setFileUpload] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const userPermission = useAppSelector(getUserPermission);
  const user: User = useAppSelector(getUserDetails);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file: File = event.target.files![0];
    setShowErrorBox(false);
    if (file) {
      if (validateFileExtensions(file.type)) {
        if (file.size <= maxFileSize) {
          setFileUpload(true)
          await dispatch(uploadPaymentFile(file, user.companyId)).then((res: any) => {
            const uploadFile = res?.file
            if (uploadFile) {
              dispatch(
                setBeneficiary({
                  name: 'formalBankApproveFile',
                  value: uploadFile,
                })
              );
            } else {
              setErrorText(t(`servers.errors.codes.${res?.error_number || 1303}`))
              setShowErrorBox(true);
            }
            //dispatch(setPay({ name: PayEnum.invoiceFile, value: files }));
          }).catch((err) => {
            setErrorText(t(`servers.errors.codes.1303`))
            setShowErrorBox(true);
          }).finally(() => {
            setFileUpload(false)
          })
        } else {
          dispatch(
            setBeneficiary({
              name: 'formalBankApproveFile',
              value: undefined,
            })
          );
          setErrorText(t(`servers.errors.codes.1302`));
          setShowErrorBox(true);
        }
      } else {
        setErrorText(t(`errors.files.upload.file_type_not_supported.title`));
        dispatch(
          setBeneficiary({
            name: 'formalBankApproveFile',
            value: undefined,
          })
        );
        setShowErrorBox(true);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    props.handleChange(event);
  };

  return (
    <Box component="form" noValidate>
      <FormCard>
        <FormHeader1
          title={t(`${prefixFields}formalBankApprove.title`)}
          text={t(`${prefixFields}formalBankApprove.text`)}
        />
        <Stack spacing={1}>
          <Stack gap={3} direction="column" overflow="hidden">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                backgroundColor: theme.palette.root.brandSecondary_012,
                padding: '32px',
                borderRadius: '8px',
                [theme.breakpoints.down('sm')]: {
                  padding: '16px',
                },
              }}
            >

              {!beneficiary.formalBankApproveFile ? (
                <HeaderPage4
                  title={t(
                    `${balanceTransStepsPrefix}.2.currency_approval.reference_doc.no_file_selected.title`
                  )}
                  text={t(
                    `${balanceTransStepsPrefix}.2.currency_approval.reference_doc.no_file_selected.text`
                  )}
                />
              ) : (
                <Typography sx={{ overflowWrap: 'break-word', maxWidth: '50%' }}>
                  {decodeURI(beneficiary?.formalBankApproveFile?.originalFileName)}
                </Typography>
              )}
              <FormButton>
                <Button
                  variant="outlined"
                  sx={{
                    '&.MuiButton-outlined': {
                      borderColor: theme.palette.root.basicDeepGreen,
                    },
                  }}
                  disabled={fileUpload}
                  component="label"
                  startIcon={
                    beneficiary?.formalBankApproveFile
                      ? imageUrl(
                        'page.balance.add.step2.currency_approval_form.file_upload_button.success.icon'
                      ) || <CheckCircleOutlineRounded />
                      : imageUrl(
                        'page.balance.add.step2.currency_approval_form.file_upload_button.upload.icon'
                      ) || <UploadFile />
                  }
                >
                  {t(
                    `${balanceTransStepsPrefix}.2.currency_approval.reference_doc.file_upload_button.text`
                  )}
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    hidden
                    accept={acceptableFileFormats}
                  />
                </Button>
              </FormButton>
            </Stack>
          </Stack>
          {showErrorBox && (
            <ErrorBox
              icon={
                <img
                  src={
                    imageUrl(
                      'page.balance.add.step2.currency_approval_form.upload.fail.icon'
                    ) || '/icons/file_error.svg'
                  }
                />
              }
              text={errorText}
            />
          )}
        </Stack>
      </FormCard>
    </Box>
  );
}
